<template>
  <div class="breakdown">
    <h2 class="show-in-pdf-only" :style="{padding: '20px'}">
      {{ 'Payout breakdown: #' + deposit.id }}
    </h2>
    <table aria-describedby="Show deposit breakdown">
      <tr class="table-header">
        <th>
          {{ $t(`deposits.breakdown.header.component`) }}
        </th>
        <th class="number">
          {{ $t(`deposits.breakdown.header.number`) }}
        </th>
        <th class="number">
          {{ $t(`deposits.breakdown.header.amount`) }}
        </th>
      </tr>
      <tr v-if="deposit.breakdown.paymentsAmount !== null">
        <td>
          {{ $t(`deposits.breakdown.payments`) }}
        </td>
        <td class="number">
          <ToolTip
            v-if="deposit.breakdown.numberOfPayments > 0"
            placement="top"
            :arrow="false"
            :content="$t(`deposits.breakdown.viewTransactionsTooltip`)"
          >
            <c-link
              class="payments-count-link"
              @click="viewTransactions"
              :id="getElementId('view_transactions')"
            >
              {{ deposit.breakdown.numberOfPayments }}
            </c-link>
          </ToolTip>
          <span v-else>
            {{ deposit.breakdown.numberOfPayments }}
          </span>
        </td>
        <td class="number">
          {{ formatMoney(deposit.breakdown.paymentsAmount) }}
        </td>
      </tr>
      <tr v-if="deposit.breakdown.total_fee !== null">
        <td>
          {{ $t(`deposits.breakdown.paymentsProcessingFee`) }}
        </td>
        <td class="number">
        </td>
        <td class="number">
          {{ formatMoney(deposit.breakdown.totalFee * -1) }}
        </td>
      </tr>
      <tr v-if="deposit.breakdown.reserveAdjustmentDebitsAmount">
        <td>
          {{ $t(`deposits.breakdown.reserveAdjustments`) }}
        </td>
        <td class="number">
          {{ deposit.breakdown.numberOfReserveAdjustmentDebits }}
        </td>
        <td class="number">
          {{ formatMoney(deposit.breakdown.reserveAdjustmentDebitsAmount * -1) }}
        </td>
      </tr>
      <tr v-if="deposit.breakdown.chargebackCreditsAmount != null">
        <td>
          {{ $t(`deposits.breakdown.credits.fieldName`) }}
          <ToolTip
            placement="top-start"
            :arrow="false"
            :content="$t(`deposits.breakdown.credits.tooltip`)"
          >
            <img alt="dropdown" class="statistics-info-icon" src="@/app/assets/images/info-outline.svg" />
          </ToolTip>
        </td>
        <td class="number">
          {{ deposit.breakdown.numberOfChargebackCredits }}
        </td>
        <td class="number">
          {{ formatMoney(deposit.breakdown.chargebackCreditsAmount) }}
        </td>
      </tr>
      <tr v-if="deposit.breakdown.refundsAmount != null">
        <td>
          {{ $t(`deposits.breakdown.refunds`) }}
        </td>
        <td class="number">
          {{ deposit.breakdown.numberOfRefunds }}
        </td>
        <td class="number">
          {{ formatMoney(deposit.breakdown.refundsAmount * -1) }}
        </td>
      </tr>
      <tr v-if="deposit.breakdown.chargebackDebitsAmount != null">
        <td>
          {{ $t(`deposits.breakdown.chargebacks`) }}
        </td>
        <td class="number">
          {{ deposit.breakdown.numberOfChargebackDebits }}
        </td>
        <td class="number">
          {{ formatMoney(deposit.breakdown.chargebackDebitsAmount * -1) }}
        </td>
      </tr>
      <tr v-if="deposit.breakdown.chargebackFeesAmount != null">
        <td>
          {{ $t(`deposits.breakdown.chargebackFees`) }}
        </td>
        <td class="number">
        </td>
        <td class="number">
          {{ formatMoney(deposit.breakdown.chargebackFeesAmount * -1) }}
        </td>
      </tr>
    </table>
    <div class="total">
      <div class="total-title">
        {{ $t(`deposits.breakdown.depositAmount`) }}
      </div>
      <div class="total-amount">
        {{ getDepositAmount() }}
      </div>
    </div>
  </div>
</template>
<script>
import Formatter from "@/app/utils/common/functions/formatter";
import DepositWithBreakdownDto from "@/api/paymentApi";
import ToolTip from "@/components/common/ToolTip.vue";
import {navigateWithParam} from "@/router";
import {RouteConstants} from "@/router/routeConstants";
import {PendoPrefixes} from "@/app/utils/common/constants";

export default {
  name: "ExportableDepositBreakdown",
  components: {ToolTip},
  props: {
    deposit: {
      type: DepositWithBreakdownDto,
      required: true,
    },
  },
  methods: {
    viewTransactions() {
      navigateWithParam(RouteConstants.TRANSACTIONS, {}, {
        ...this.$route.query,
        deposit_id: this.deposit.id
      });
    },
    getElementId(id) {
      return PendoPrefixes.CBpay + this.$route.name + '-' + id;
    },
    formatDateWithTimezone(date) {
      return Formatter.format({type: 'zonedDateWithTime', data: date})
    },
    format(type, data, format, currencyCode) {
      return Formatter.format({type, data, format, currencyCode})
    },
    formatMoney(amount) {
      return Formatter.format({
        type: 'currency',
        data: amount,
        currencyCode: this.deposit.currency,
      }) + " " + this.deposit.currency
    },
    getMerchantDepositAmount() {
      return this.deposit.type === 'DEBIT' ? this.deposit.amount * (-1) : this.deposit.amount;
    },
    getDepositAmount() {
      if (this.getMerchantDepositAmount() === undefined) {
        return null;
      }
      return Formatter.format({
        type: 'currency',
        data: this.getMerchantDepositAmount(),
        currencyCode: this.deposit.currency,
      }) + " " + this.deposit.currency
    },
  }
}
</script>
<style lang="scss">
.breakdown {

  & table {
    border: 1px solid #EEF1F7;
    border-radius: 8px;
    border-collapse: inherit;
    width: 100%;
    height: 100%;
    text-align: left;
    border-spacing: 0;

    & th {
      font-weight: $weight_normal;
      background: #EEF1F7;
    }

    & th:nth-child(1) {
      border-radius: 8px 0px 0px 0px;
    }

    & th:nth-child(3) {
      border-radius: 0px 8px 0px 0px;
    }

    & td, th {
      padding: 14px 24px;
      border-bottom: 1px solid #EEF1F7;
    }

    & tr:last-child {
      & td {
        border-bottom: none;
      }
    }

    & .number {
      text-align: right;
    }

    & .payments-count-link {
      font-weight: $weight_bold;
      color: $primary_600;
      cursor: pointer;
    }
  }

  & .total {
    width: 350px;
    margin-top: 24px;
    margin-right: 24px;
    height: 30px;
    display: flex;
    float: right;
    justify-content: space-between;
    align-items: center;
    font-weight: $weight_bold;
    font-size: 14px;

    & .total-amount {
      font-size: 16px;
    }
  }
}
</style>